import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { tournaments as tournamentsApi } from "../lib/api";
import { NearbyGamesSection } from "../components/NearbyGamesSection";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { GAMES, FORMATS, getGameColor, getGameName } from "../constants/theme";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, Users, CheckCircle, Trophy, Dices } from "lucide-react";

const formatDate = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

const PLAYERS = [
  { value: "all", label: "Nombre de joueurs" },
  { value: "0-8", label: "≤ 8 joueurs" },
  { value: "9-16", label: "9 – 16 joueurs" },
  { value: "17-32", label: "17 – 32 joueurs" },
  { value: "33+", label: "33+ joueurs" },
];

const FULLNESS = [
  { value: "all", label: "Complets & disponibles" },
  { value: "available", label: "Masquer les complets" },
  { value: "full", label: "Complets uniquement" },
];

const DEFAULT_FILTERS = { game: "all", format: "all", players: "all", fullness: "all" };

const applyFilters = (list, f) => list.filter((t) => {
  if (f.game !== "all" && t.game !== f.game) return false;
  if (f.format !== "all" && t.format !== f.format) return false;
  if (f.players !== "all") {
    const cap = t.capacity || 0;
    if (f.players === "0-8" && !(cap > 0 && cap <= 8)) return false;
    if (f.players === "9-16" && !(cap >= 9 && cap <= 16)) return false;
    if (f.players === "17-32" && !(cap >= 17 && cap <= 32)) return false;
    if (f.players === "33+" && !(cap >= 33)) return false;
  }
  if (f.fullness !== "all") {
    const count = (t.participants || []).length;
    const isFull = t.capacity && count >= t.capacity;
    if (f.fullness === "available" && isFull) return false;
    if (f.fullness === "full" && !isFull) return false;
  }
  return true;
});

const triggerCls = "w-auto min-w-[140px] h-9 bg-[#252525] border-[#333] text-white text-xs";
const contentCls = "bg-[#1a1a1a] border-[#333] text-white";

function FilterBar({ filters, onChange, idPrefix }) {
  const set = (patch) => onChange({ ...filters, ...patch });
  const formats = FORMATS[filters.game] || [];
  return (
    <div className="flex flex-wrap gap-2 mb-3" data-testid={`${idPrefix}-filters`}>
      <Select value={filters.game} onValueChange={(v) => set({ game: v, format: "all" })}>
        <SelectTrigger className={triggerCls} data-testid={`${idPrefix}-filter-game`}><SelectValue placeholder="Type de jeu" /></SelectTrigger>
        <SelectContent className={contentCls}>
          <SelectItem value="all">Tous les jeux</SelectItem>
          {GAMES.map((g) => <SelectItem key={g.id} value={g.id}>{g.shortName}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={filters.format} onValueChange={(v) => set({ format: v })} disabled={filters.game === "all" || formats.length === 0}>
        <SelectTrigger className={triggerCls} data-testid={`${idPrefix}-filter-format`}><SelectValue placeholder="Format de jeu" /></SelectTrigger>
        <SelectContent className={contentCls}>
          <SelectItem value="all">Tous les formats</SelectItem>
          {formats.map((fm) => <SelectItem key={fm} value={fm}>{fm}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={filters.players} onValueChange={(v) => set({ players: v })}>
        <SelectTrigger className={triggerCls} data-testid={`${idPrefix}-filter-players`}><SelectValue placeholder="Nombre de joueurs" /></SelectTrigger>
        <SelectContent className={contentCls}>
          {PLAYERS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={filters.fullness} onValueChange={(v) => set({ fullness: v })}>
        <SelectTrigger className={triggerCls} data-testid={`${idPrefix}-filter-fullness`}><SelectValue placeholder="Complets" /></SelectTrigger>
        <SelectContent className={contentCls}>
          {FULLNESS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function TournamentRow({ t, to, trailing, testid }) {
  const count = (t.participants || []).length;
  return (
    <Link to={to} className="flex items-center gap-3 p-2.5 rounded-lg bg-[#252525] hover:bg-[#2e2e2e] transition-colors" data-testid={testid}>
      <div className="w-1 h-9 rounded-full shrink-0" style={{ backgroundColor: getGameColor(t.game) }} />
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">{t.name}</p>
        <div className="flex items-center gap-3 text-[11px] text-[#666] mt-0.5 flex-wrap">
          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(t.date)}</span>
          {t.venue?.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{t.venue.city}</span>}
          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{count}/{t.capacity || "?"}</span>
          {t.format && <span className="hidden sm:inline">{t.format}</span>}
        </div>
      </div>
      <Badge style={{ backgroundColor: `${getGameColor(t.game)}20`, color: getGameColor(t.game) }} className="text-[10px] shrink-0">{getGameName(t.game)}</Badge>
      {trailing}
    </Link>
  );
}

export default function PlayerDiscoverPage() {
  const { user, isAuthenticated, isAdmin, isOrg, hasShop } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [toClose, setToClose] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upcomingFilters, setUpcomingFilters] = useState(DEFAULT_FILTERS);
  const [closingFilters, setClosingFilters] = useState(DEFAULT_FILTERS);

  const canOrganize = isAdmin || isOrg || hasShop;

  useEffect(() => {
    setLoading(true);
    tournamentsApi.list({ limit: 200 })
      .then((r) => setTournaments(r.data || []))
      .catch(() => toast.error("Erreur chargement des tournois"))
      .finally(() => setLoading(false));
  }, []);

  // Organizer's tournaments needing closure
  useEffect(() => {
    if (!isAuthenticated || !canOrganize) { setToClose([]); return; }
    const params = isAdmin ? { limit: 200 } : { organizer_id: user?.id, limit: 200 };
    tournamentsApi.list(params).then((r) => {
      const all = r.data || [];
      const now = new Date();
      const overdue = (t) => t.date && new Date(t.date) < now && (t.status || "upcoming") === "upcoming";
      setToClose(all.filter((t) => t.status === "completed" || overdue(t)));
    }).catch(() => setToClose([]));
  }, [isAuthenticated, isAdmin, isOrg, hasShop, user?.id]);

  const now = new Date();
  const upcoming = applyFilters(
    tournaments.filter((t) => (t.status || "upcoming") === "upcoming" && t.date && new Date(t.date) >= now),
    upcomingFilters,
  ).sort((a, b) => new Date(a.date) - new Date(b.date));

  const closing = applyFilters(toClose, closingFilters).sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      {/* Greeting */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
            <Trophy className="w-6 h-6 text-[#FF6B35]" />Bonjour {user?.pseudo || user?.name || ""} !
          </h1>
          <p className="text-[#666] text-xs sm:text-sm mt-0.5">Vos tournois à venir et à clôturer</p>
        </div>
        <Link to="/player/create-game" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#7B68EE] hover:bg-[#6A5ACD] text-white text-sm font-semibold transition-colors shrink-0" data-testid="create-game-btn">
          <Dices className="w-4 h-4" />Créer une partie
        </Link>
      </div>

      {/* Tournois à venir */}
      <section className="bg-[#1a1a1a] border border-[#333] rounded-xl p-4" data-testid="home-upcoming-section">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-5 h-5 text-[#FF6B35]" />
          <h2 className="text-base font-bold text-white">Les tournois à venir</h2>
          <span className="ml-auto text-xs text-[#666]">{upcoming.length}</span>
        </div>
        <FilterBar filters={upcomingFilters} onChange={setUpcomingFilters} idPrefix="upcoming" />
        {loading ? (
          <div className="flex items-center justify-center h-24"><div className="animate-spin rounded-full h-7 w-7 border-b-2 border-[#FF6B35]" /></div>
        ) : upcoming.length === 0 ? (
          <p className="text-[#666] text-sm py-6 text-center">Aucun tournoi à venir</p>
        ) : (
          <div className="space-y-2">
            {upcoming.map((t) => (
              <TournamentRow key={t.id} t={t} to={`/player/event/tournament/${t.id}`} testid={`upcoming-${t.id}`} />
            ))}
          </div>
        )}
      </section>

      {/* Tournois à clôturer (organisateurs) */}
      {canOrganize && (
        <section className="bg-[#1a1a1a] border border-[#333] rounded-xl p-4" data-testid="home-toclose-section">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-amber-400" />
            <h2 className="text-base font-bold text-white">Les tournois à clôturer</h2>
            <span className="ml-auto text-xs text-[#666]">{closing.length}</span>
          </div>
          <FilterBar filters={closingFilters} onChange={setClosingFilters} idPrefix="closing" />
          {closing.length === 0 ? (
            <p className="text-[#666] text-sm py-6 text-center">Aucun tournoi à clôturer</p>
          ) : (
            <div className="space-y-2">
              {closing.map((t) => (
                <TournamentRow
                  key={t.id} t={t} to="/shop/tournaments?tab=toclose" testid={`toclose-${t.id}`}
                  trailing={<Badge className="bg-amber-500/15 text-amber-400 text-[10px] shrink-0 gap-1"><CheckCircle className="w-3 h-3" />À clôturer</Badge>}
                />
              ))}
            </div>
          )}
        </section>
      )}

      <NearbyGamesSection isAuthenticated={true} />
    </div>
  );
}
